import { AnimatePresence, motion } from 'framer-motion';
import { HelpCircle, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { isIOS, isStandalone } from '../utils/pwa';

export const AddToHomeScreen = () => {
  const [isAvailable, setIsAvailable] = useState(false);
  const [show, setShow] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    setIsAvailable(isIOS() && !isStandalone());
  }, []);

  useEffect(() => {
    if (!show) return;

    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShow(false);
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled])'),
      );
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      triggerRef.current?.focus();
    };
  }, [show]);

  if (!isAvailable) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setShow(true)}
        aria-label="ホーム画面への追加方法"
        title="ホーム画面への追加方法"
        aria-haspopup="dialog"
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-current/10 opacity-75 transition-colors hover:bg-black/5 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:hover:bg-white/10"
      >
        <HelpCircle size={19} aria-hidden="true" />
      </button>

      <AnimatePresence>
        {show && (
          <div className="fixed inset-0 z-[90] flex items-end justify-center p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
            <button
              type="button"
              onClick={() => setShow(false)}
              aria-label="ホーム画面追加の説明を閉じる"
              className="absolute inset-0 bg-black/45 backdrop-blur-sm"
            />
            <motion.dialog
              ref={dialogRef}
              open
              aria-modal="true"
              aria-labelledby="home-screen-title"
              initial={{ opacity: 0, y: 36, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ type: 'spring', damping: 26, stiffness: 260 }}
              className="relative my-0 mb-2 max-h-[85dvh] w-full max-w-sm overflow-y-auto rounded-3xl border border-neutral-700 bg-neutral-900 p-5 text-neutral-100 shadow-2xl"
            >
              <div className="mb-4 flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-amber-200/20 bg-gradient-to-br from-amber-100 to-amber-200 shadow-inner">
                    <span className="select-none font-serif text-xl font-bold text-amber-900">
                      N
                    </span>
                  </div>
                  <div>
                    <h2 id="home-screen-title" className="text-base font-bold tracking-tight">
                      NovelFlowをホーム画面へ
                    </h2>
                    <p className="mt-1 text-sm text-neutral-400">アプリのように全画面で読書</p>
                  </div>
                </div>
                <button
                  ref={closeButtonRef}
                  type="button"
                  onClick={() => setShow(false)}
                  aria-label="閉じる"
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-neutral-800 text-neutral-300 transition-colors hover:bg-neutral-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
                >
                  <X size={18} aria-hidden="true" />
                </button>
              </div>

              <p className="text-sm leading-relaxed text-neutral-300">
                Safariの共有メニューからホーム画面に追加すると、ブラウザのアドレスバーを隠して読めます。
              </p>

              <ol className="mt-4 space-y-3 rounded-2xl border border-neutral-800 bg-black/30 p-4 text-sm leading-relaxed">
                <li className="flex items-start gap-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-neutral-800 font-bold text-amber-200">
                    1
                  </span>
                  <span>Safari下部の共有ボタンをタップします。</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-neutral-800 font-bold text-amber-200">
                    2
                  </span>
                  <span>メニューから「ホーム画面に追加」を選びます。</span>
                </li>
              </ol>
            </motion.dialog>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
